// Omnichannel channel connections (IR-68). Ported from demo-ui
// src/data/mock/inbox.ts — the channel list/brand metadata is shared with the
// upcoming Inbox (IR-69); the connection types are the Settings → Channels
// contract the backend will implement.

export type TChannel = "whatsapp" | "instagram" | "messenger" | "telegram" | "webchat" | "sms";

export type TChannelMeta = {
  label: string;
  /** Short badge label (IG / WA / …). */
  short: string;
  /** Brand colour. */
  color: string;
};

export const CHANNEL_META: Record<TChannel, TChannelMeta> = {
  whatsapp: { label: "WhatsApp", short: "WA", color: "#25D366" },
  instagram: { label: "Instagram", short: "IG", color: "#E1306C" },
  messenger: { label: "Messenger", short: "FB", color: "#0084FF" },
  telegram: { label: "Telegram", short: "TG", color: "#2AABEE" },
  webchat: { label: "WebChat", short: "Web", color: "#3B82F6" },
  sms: { label: "SMS", short: "SMS", color: "#14B8A6" },
};

/** A connectable provider. Meta covers both Instagram + Messenger via one OAuth. */
export type TConnectableProvider = "meta" | "whatsapp" | "telegram";

export type TConnectionStatus = "connected" | "disconnected" | "error";

export type TConnectedAccount = {
  /** Which channel the account belongs to (Meta returns an IG account + a FB Page). */
  channel: TChannel;
  label: string;
  value: string;
};

export type TChannelConnection = {
  id: string;
  provider: TConnectableProvider;
  status: TConnectionStatus;
  /** Connected accounts/pages/numbers shown under the card. */
  accounts: TConnectedAccount[];
  /**
   * Channels of this provider that currently receive DMs. Meta connects IG and
   * Messenger with one login; each can be switched off without disconnecting.
   */
  enabledChannels: TChannel[];
  /** Populated when status is "error" (token expired, access revoked…). */
  error?: string;
  /** The card couldn't be loaded (network / server error): shown as an error, but not a broken connection. */
  loadFailed?: boolean;
  connectedAt?: string;
  /** WhatsApp only: a company can connect several numbers (Sales, Support, agents). */
  numbers?: TWhatsAppNumber[];
  /** Meta only: connected Facebook Pages with their Instagram accounts. */
  pages?: TMetaPage[];
};

export const PROVIDER_META: Record<
  TConnectableProvider,
  { label: string; channels: TChannel[]; blurb: string }
> = {
  meta: {
    label: "Meta",
    channels: ["instagram", "messenger"],
    blurb: "Instagram DMs and Facebook Messenger through your Facebook Page.",
  },
  whatsapp: {
    label: "WhatsApp Business",
    channels: ["whatsapp"],
    blurb: "Connect a verified WhatsApp Business number.",
  },
  telegram: {
    label: "Telegram",
    channels: ["telegram"],
    blurb: "Connect a Telegram bot with a token from @BotFather.",
  },
};

/** GET /integrations/meta-inbox/config — public values to launch Facebook Login for Business. */
export type TMetaInboxConfig = {
  enabled: boolean;
  appId?: string;
  configId?: string;
  graphVersion: string;
};

/**
 * A Facebook Page connected through "Connect with Meta" (GET /integrations/meta-inbox/pages):
 * Messenger, plus Instagram DMs when an Instagram professional account is linked. Never carries the token.
 */
export type TMetaPage = {
  id: number;
  pageId: string;
  pageName?: string;
  igUserId?: string;
  igUsername?: string;
  igProfilePictureUrl?: string;
  /** False while Instagram DMs are paused for this Page. */
  instagramEnabled: boolean;
  /** False while Messenger is paused for this Page. */
  messengerEnabled: boolean;
  status: TConnectionStatus;
  lastError?: string;
  needsReauth: boolean;
  connectedAt?: string;
};

/**
 * Result of Meta's WhatsApp Embedded Signup. `code` is single-use and expires
 * after ~30 s, so it goes to the backend straight away.
 */
export type TWhatsAppSignupResult = {
  code: string;
  wabaId: string;
  phoneNumberId: string;
  businessId?: string;
  /** The SDK dialog's redirect_uri; Meta binds the code to it. */
  redirectUri?: string;
};

export type TConnectWhatsAppPayload = TWhatsAppSignupResult & { label?: string };

/** GET /integrations/whatsapp/config — public values to launch Embedded Signup. */
export type TWhatsAppConfig = {
  enabled: boolean;
  appId?: string;
  configId?: string;
  graphVersion: string;
};

/** One of the company's WhatsApp numbers (GET /integrations/whatsapp/numbers). Never carries the token. */
export type TWhatsAppNumber = {
  id: number;
  label?: string;
  /** Set when this is a user's own number (Settings → Account); absent for a shared company number. */
  ownerUserId?: number;
  ownerName?: string;
  status: TConnectionStatus;
  wabaId?: string;
  phoneNumberId?: string;
  businessId?: string;
  displayPhoneNumber?: string;
  verifiedName?: string;
  qualityRating?: string;
  messagingLimit?: string;
  lastError?: string;
  needsReauth: boolean;
  connectedAt?: string;
};

export type TConnectTelegramPayload = {
  token: string;
};
